'use client'

import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { formatCookTime } from '@repo/ui/src/functions/recipe-functions';
import { MediaType, MetaProps } from '@repo/ui/src/types';
import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import RatingStars from '@repo/ui/src/components/RatingStars';
import Button, { ButtonVariant } from '../Button';
import { BackgroundStyle } from './index';
import { classNames } from '@repo/ui/src/utils/classNames';
import KnifeIcon from '@repo/ui/src/components/Icons/KnifeIcon';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useId } from 'react';

interface RecipeCardProps {
  _meta: MetaProps;
  recipeMetaSection: {
    title: string;
    description?: string;
    cookTime?: number;
    prepTime?: number;
    complexity?: string;
    hardwood?: string;
    isFeatured?: boolean;
    product?: string;
    servingSizeMin?: number;
    servingSizeMax?: number;
  };
  assets: Array<MediaType>;
  ratingSummary?: {
    average: number;
    count: number;
  };
  backgroundStyle?: BackgroundStyle;
}

const RecipeCard = ({
  recipeMetaSection,
  assets,
  _meta,
  ratingSummary,
  backgroundStyle = 'Light',
}: RecipeCardProps) => {
  const imageUrl = buildImageSrc(assets?.length ? assets[0] : null, 700, 2, 1);
  const link = _meta?.deliveryKey || '';
  const titleId = `recipe-card-title${useId()}`;
  const { t } = useTranslation();

  if (!recipeMetaSection) return null;

  const totalTime = (recipeMetaSection.cookTime || 0) + (recipeMetaSection.prepTime || 0);
  const timeDisplay = formatCookTime(totalTime);
  const isDark = backgroundStyle === 'Dark';

  return (
    <article aria-labelledby={titleId} className="relative h-full flex flex-col group mb-2">
      <Button
        link={link}
        as={Anchor}
        variant={ButtonVariant.bare}
        className="w-full flex-grow flex flex-col"
        size="none"
        ariaLabelledBy={titleId}
      >
        <div className="relative w-full aspect-[4/3] overflow-hidden">
          <Image
            src={imageUrl}
            alt={recipeMetaSection?.title || ''}
            layout="fill"
            objectFit="cover"
            className="group-hover:scale-105 transition-transform duration-1000"
            data-testid="recipe-card-image"
          />
        </div>

        <div className="flex flex-col flex-grow justify-between pt-6 px-4">
          <div className="flex flex-col gap-2">
            <h3 id={titleId} className={classNames('tra-text-web-sub-2', isDark && 'text-white')}>
              {recipeMetaSection?.title}
            </h3>

            {ratingSummary && ratingSummary.average > 0 && (
              <div className="flex items-center gap-2">
                <RatingStars value={ratingSummary.average} hideValue size="small" dark={isDark} />
                {ratingSummary.count > 0 && (
                  <span
                    className={classNames('text-sm', isDark ? 'text-white' : 'text-neutral-600')}
                  >
                    {ratingSummary.count}
                  </span>
                )}
              </div>
            )}

            {timeDisplay && (
              <div
                className={classNames(
                  'tra-text-web-body-m lowercase',
                  isDark ? 'text-neutral-200' : 'text-neutral-400',
                )}
              >
                {timeDisplay}
              </div>
            )}
          </div>

          <div
            aria-hidden="true"
            className="tra-btn-special tra-text-web-utl-button-s pt-4 pb-4 flex w-full items-center justify-between tracking-normal group-hover:text-brand-300 transition-colors duration-300"
          >
            {t('recipeCard.viewRecipe', undefined, { default: 'View Recipe' })}
            <KnifeIcon
              className={classNames(
                'tra-btn-icon-special group-hover:text-brand-300',
                isDark ? 'text-white' : 'text-neutral-800',
              )}
              aria-hidden="true"
            />
          </div>
        </div>
      </Button>
    </article>
  );
};

export default RecipeCard;
